import { useState } from 'react'

import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Spinner from 'react-bootstrap/Spinner'

// Stella returns a compact, library-agnostic report payload
// ({ reportId, title, format, table, export }). The table is the part the chat
// cannot already show through the reply or charts, so render it inline and let
// the admin download exactly what they see in any format.
const FORMAT_LABEL = {
  summary: 'Summary',
  table: 'Table',
  chart: 'Chart',
  csv: 'CSV',
  excel: 'Excel',
  pdf: 'PDF',
}

const DOWNLOAD_FORMATS = ['csv', 'excel', 'pdf']

const asArray = (value) => (Array.isArray(value) ? value : [])

const cellText = (value) => {
  if (value === null || value === undefined) return ''
  return String(value)
}

export default function StellaReport({ report, onExport }) {
  const [busyFormat, setBusyFormat] = useState(null)
  const [links, setLinks] = useState({})
  const [error, setError] = useState('')

  if (!report) return null

  const columns = asArray(report.table?.columns)
  const rows = asArray(report.table?.rows)
  const hasTable = columns.length > 0 && rows.length > 0
  const serverExport = report.export

  if (!hasTable && !serverExport) return null

  const canDownload = hasTable && typeof onExport === 'function'
  const isFullDataset =
    report.reportId === 'order-listings' &&
    Boolean(report.source?.from && report.source?.to)

  const handleExport = async (format) => {
    if (busyFormat) return
    setError('')
    const existing = links[format]
    if (existing?.url) {
      window.open(existing.url, '_blank', 'noopener,noreferrer')
      return
    }
    setBusyFormat(format)
    try {
      const result = await onExport(format, report)
      if (!result?.url) throw new Error('No file was returned')
      setLinks((current) => ({ ...current, [format]: result }))
      window.open(result.url, '_blank', 'noopener,noreferrer')
    } catch (exportError) {
      setError(
        exportError?.response?.data?.error?.message ||
          exportError?.message ||
          'Could not export that report.'
      )
    } finally {
      setBusyFormat(null)
    }
  }

  return (
    <section className="draft-events-assistant__report">
      <div className="draft-events-assistant__report-head">
        <strong>{report.title || 'Report'}</strong>
        {report.format ? (
          <Badge bg="info">
            {FORMAT_LABEL[report.format] || report.format}
          </Badge>
        ) : null}
      </div>
      {report.source?.from || report.source?.eventUuid ? (
        <p className="draft-events-assistant__report-source">
          {[
            report.source.from
              ? `From ${new Date(report.source.from).toLocaleString()}`
              : null,
            report.source.to
              ? `to ${new Date(report.source.to).toLocaleString()}`
              : null,
            report.source.eventUuid ? 'event-scoped' : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      ) : null}

      {hasTable ? (
        <div className="draft-events-assistant__report-scroll">
          <table className="draft-events-assistant__report-table">
            <thead>
              <tr>
                {columns.map((column, index) => (
                  <th key={`col-${index}`}>{cellText(column)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={`row-${rowIndex}`}>
                  {columns.map((_column, colIndex) => (
                    <td key={`cell-${rowIndex}-${colIndex}`}>
                      {cellText(asArray(row)[colIndex])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {serverExport?.url ? (
        <a
          className="draft-events-assistant__report-download"
          href={serverExport.url}
          target="_blank"
          rel="noreferrer"
          download={serverExport.filename || undefined}
        >
          Download {FORMAT_LABEL[serverExport.format] || serverExport.format}
        </a>
      ) : null}

      {canDownload ? (
        <div className="draft-events-assistant__report-actions">
          <span className="draft-events-assistant__report-actions-label">
            {isFullDataset ? 'Download full dataset:' : 'Download:'}
          </span>
          {DOWNLOAD_FORMATS.map((format) => (
            <Button
              key={format}
              size="sm"
              variant={links[format]?.url ? 'success' : 'outline-secondary'}
              disabled={Boolean(busyFormat)}
              onClick={() => handleExport(format)}
            >
              {busyFormat === format ? (
                <Spinner as="span" size="sm" animation="border" />
              ) : (
                FORMAT_LABEL[format]
              )}
            </Button>
          ))}
        </div>
      ) : null}

      {error ? (
        <div className="draft-events-assistant__report-error">{error}</div>
      ) : null}
    </section>
  )
}
